import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import Select from 'react-select';
import useBgPages from '../../../hooks/use-bg-pages.hook';
import Asaid from '../../../components/layout/asaid.component';
import Header from '../../../components/layout/header.component';
import Footer from '../../../components/layout/footer.component';
import Icon from '../../../components/forms/icon.component';
import Input from '../../../components/forms/input.component';
import DatePicker from '../../../components/forms/date-picker.component';
import { AlertService } from '../../../services/alert.service';
import {
  AUDIT_ACTIONS,
  AUDIT_ENTITY_TYPES,
  AuditService,
  type AuditList,
  type AuditLog,
  type AuditQuery
} from '../../../services/audit.service';

const ENTITY_LABEL: Record<string, string> = {
  auth: 'Autenticación',
  user: 'Usuario',
  cpd_template: 'CPD template',
  cpd_operator: 'CPD operador',
  cpd_route: 'CPD ruta',
  cpd_route_operator: 'CPD override'
};
const ACTION_BADGE: Record<string, string> = {
  login: 'badge-light-info',
  logout: 'badge-light',
  create: 'badge-light-success',
  update: 'badge-light-warning',
  delete: 'badge-light-danger',
  reload: 'badge-light-primary',
  password_change: 'badge-light-dark',
  password_reset_request: 'badge-light-dark',
  password_reset_consume: 'badge-light-dark'
};
const PAGE_SIZES = [50, 100, 200];

interface Filters {
  from: string;
  to: string;
  entityType: string;
  action: string;
  actorId: string;
  entityId: string;
  success: '' | 'true' | 'false';
}
const DEFAULT: Filters = {
  from: moment().format('YYYY-MM-DD'),
  to: moment().format('YYYY-MM-DD'),
  entityType: '',
  action: '',
  actorId: '',
  entityId: '',
  success: ''
};

const fmt = (iso: string) =>
  new Date(iso).toLocaleString('es-MX', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

/** Diff plano before/after: solo llaves que cambian (o todas si solo hay un lado). */
function diffRows(before?: Record<string, unknown>, after?: Record<string, unknown>) {
  const keys = Array.from(
    new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])
  ).filter((k) => k !== 'password');
  return keys
    .map((k) => ({ k, b: before?.[k], a: after?.[k] }))
    .filter(({ b, a }) => JSON.stringify(b) !== JSON.stringify(a));
}
const show = (v: unknown) =>
  v === undefined ? (
    <span className="text-muted">—</span>
  ) : typeof v === 'object' ? (
    JSON.stringify(v)
  ) : (
    String(v)
  );

const AuditPage: React.FC = () => {
  useBgPages();
  const [draft, setDraft] = useState<Filters>(DEFAULT);
  const [applied, setApplied] = useState<Filters>(DEFAULT);
  const [limit, setLimit] = useState(50);
  const [data, setData] = useState<AuditList | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async (f: Filters, l: number, offset: number) => {
    try {
      setLoading(true);
      const q: AuditQuery = {
        from: f.from,
        to: f.to,
        entityType: f.entityType || undefined,
        action: f.action || undefined,
        actorId: f.actorId.trim() || undefined,
        entityId: f.entityId.trim() || undefined,
        success: f.success === '' ? undefined : f.success === 'true',
        limit: l,
        offset
      };
      setData(await AuditService.search(q));
    } catch (e: any) {
      AlertService.toastError(e?.error || e?.status || 'No se pudo cargar la auditoría');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(DEFAULT, 50, 0);
  }, [load]);

  const apply = () => {
    setApplied(draft);
    load(draft, limit, 0);
  };

  const opts = (vals: readonly string[], labelOf: (v: string) => string, all: string) => [
    { value: '', label: all },
    ...vals.map((v) => ({ value: v, label: labelOf(v) }))
  ];
  const entityOpts = useMemo(
    () => opts(AUDIT_ENTITY_TYPES, (v) => ENTITY_LABEL[v] ?? v, 'Todas las entidades'),
    []
  );
  const actionOpts = useMemo(() => opts(AUDIT_ACTIONS, (v) => v, 'Todas las acciones'), []);
  const successOpts = [
    { value: '', label: 'OK y fallidas' },
    { value: 'true', label: 'Solo OK' },
    { value: 'false', label: 'Solo fallidas' }
  ];
  const sel = (o: { value: string; label: string }[], k: keyof Filters) => (
    <Select
      options={o}
      className="form-select-custom"
      classNamePrefix="form-select-custom"
      value={o.find((x) => x.value === draft[k]) ?? o[0]}
      onChange={(x: any) => setDraft({ ...draft, [k]: x?.value ?? '' })}
    />
  );

  const total = data?.total ?? 0;
  const offset = data?.offset ?? 0;
  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="page d-flex flex-row flex-column-fluid">
      <Asaid />
      <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
        <Header />
        <div
          className="content fs-6 d-flex flex-column flex-column-fluid bg-cards-p"
          id="kt_content"
        >
          <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
            <div className="container-fluid px-4 px-md-10 px-xxl-15">
              <div className="mb-5">
                <h1 className="fw-bolder fs-2x mb-1">Auditoría</h1>
                <div className="text-muted fs-7">
                  Quién cambió qué y cuándo (audit_log del backend, retención 365 días) ·{' '}
                  {applied.from} → {applied.to}
                </div>
              </div>

              <div className="card shadow-sm border-0 rounded-4">
                <div className="card-body p-5">
                  <form
                    noValidate
                    onSubmit={(e) => {
                      e.preventDefault();
                      apply();
                    }}
                  >
                    <div className="row g-4 align-items-end">
                      <div className="col-md-4 col-xl-3">
                        <label className="form-label">Rango</label>
                        <DatePicker
                          initialStart={draft.from}
                          initialEnd={draft.to}
                          onDatesChange={(r: any) =>
                            setDraft({
                              ...draft,
                              from: r.start.format('YYYY-MM-DD'),
                              to: r.end.format('YYYY-MM-DD')
                            })
                          }
                        />
                      </div>
                      <div className="col-md-4 col-xl-2">
                        <label className="form-label">Entidad</label>
                        {sel(entityOpts, 'entityType')}
                      </div>
                      <div className="col-md-4 col-xl-2">
                        <label className="form-label">Acción</label>
                        {sel(actionOpts, 'action')}
                      </div>
                      <div className="col-md-4 col-xl-2">
                        <label className="form-label">Resultado</label>
                        {sel(successOpts, 'success')}
                      </div>
                      <div className="col-md-4 col-xl-3">
                        <Input
                          label="ID del actor (usuario)"
                          placeholder="_id de Mongo"
                          value={draft.actorId}
                          onChange={(e: any) => setDraft({ ...draft, actorId: e.target.value })}
                        />
                      </div>
                      <div className="col-md-4 col-xl-3">
                        <Input
                          label="ID de la entidad"
                          placeholder="id del registro afectado"
                          value={draft.entityId}
                          onChange={(e: any) => setDraft({ ...draft, entityId: e.target.value })}
                        />
                      </div>
                      <div className="col-md-8 col-xl-9 d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          className="btn btn-light fw-bold fs-7"
                          onClick={() => setDraft({ ...DEFAULT, from: draft.from, to: draft.to })}
                        >
                          Limpiar
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary fw-bold fs-7"
                          disabled={loading}
                        >
                          <Icon icon="ki-magnifier" size="fs-4 me-1" />{' '}
                          {loading ? 'Consultando…' : 'Consultar'}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              <div className="card shadow-sm border-0 rounded-4 mt-6">
                <div className="card-body p-5">
                  <div className="table-responsive">
                    <table className="table table-row-dashed align-middle fs-7 gy-3">
                      <thead>
                        <tr className="text-muted fw-bold text-uppercase fs-8">
                          <th style={{ width: 24 }}></th>
                          <th>Cuándo</th>
                          <th>Quién</th>
                          <th>Acción</th>
                          <th>Entidad</th>
                          <th>Resultado</th>
                          <th>Request</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading && (
                          <tr>
                            <td colSpan={7} className="text-center text-muted py-10">
                              Consultando…
                            </td>
                          </tr>
                        )}
                        {!loading && data && data.items.length === 0 && (
                          <tr>
                            <td colSpan={7} className="text-center text-muted py-10">
                              Sin eventos con esos filtros
                            </td>
                          </tr>
                        )}
                        {!loading &&
                          data?.items.map((it: AuditLog) => {
                            const isOpen = open === it.id;
                            const diff = diffRows(it.before, it.after);
                            const hasDetail = diff.length > 0 || !!it.metadata || !!it.errorMessage;
                            return (
                              <React.Fragment key={it.id}>
                                <tr
                                  style={{ cursor: hasDetail ? 'pointer' : 'default' }}
                                  onClick={() => hasDetail && setOpen(isOpen ? null : it.id)}
                                >
                                  <td className="text-muted">
                                    {hasDetail && (
                                      <Icon icon={isOpen ? 'ki-down' : 'ki-right'} size="fs-6" />
                                    )}
                                  </td>
                                  <td className="text-nowrap">{fmt(it.at)}</td>
                                  <td>
                                    {it.actor?.id === 'anonymous' ? (
                                      <>
                                        <div className="fw-bold text-muted">
                                          {it.actor?.username
                                            ? `intento: ${it.actor.username}`
                                            : 'anónimo'}
                                        </div>
                                        <div className="text-muted fs-9">sin sesión</div>
                                      </>
                                    ) : (
                                      <>
                                        <div className="fw-bold">
                                          {it.actor?.username ||
                                            it.actor?.email ||
                                            it.actor?.id ||
                                            '—'}
                                        </div>
                                        <div className="text-muted fs-9">
                                          {(it.actor?.roles ?? []).join(', ')}
                                          {it.actor?.username && it.actor?.email
                                            ? ` · ${it.actor.email}`
                                            : ''}
                                        </div>
                                      </>
                                    )}
                                  </td>
                                  <td>
                                    <span
                                      className={`badge ${ACTION_BADGE[it.action] ?? 'badge-light'} fs-8`}
                                    >
                                      {it.action}
                                    </span>
                                  </td>
                                  <td>
                                    <div>{ENTITY_LABEL[it.entityType] ?? it.entityType}</div>
                                    {it.entityId && (
                                      <code className="fs-9 text-muted">{it.entityId}</code>
                                    )}
                                  </td>
                                  <td>
                                    {it.success ? (
                                      <span className="badge badge-light-success fs-8">OK</span>
                                    ) : (
                                      <span className="badge badge-light-danger fs-8">falló</span>
                                    )}
                                  </td>
                                  <td className="text-muted fs-8">
                                    <code>
                                      {it.context?.method} {it.context?.path}
                                    </code>
                                    {it.context?.ip && <div>{it.context.ip}</div>}
                                  </td>
                                </tr>
                                {isOpen && (
                                  <tr>
                                    <td></td>
                                    <td colSpan={6} className="bg-light rounded">
                                      {it.errorMessage && (
                                        <div className="alert alert-danger p-3 fs-7 mb-3">
                                          {it.errorMessage}
                                        </div>
                                      )}
                                      {diff.length > 0 && (
                                        <table className="table table-sm fs-8 font-monospace mb-2">
                                          <thead>
                                            <tr className="text-muted">
                                              <th>campo</th>
                                              <th>antes</th>
                                              <th>después</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {diff.map(({ k, b, a }) => (
                                              <tr key={k}>
                                                <td className="text-gray-600">{k}</td>
                                                <td className="text-decoration-line-through text-muted">
                                                  {show(b)}
                                                </td>
                                                <td className="fw-bold">{show(a)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      )}
                                      {it.metadata && (
                                        <details>
                                          <summary
                                            className="fs-8 text-muted"
                                            style={{ cursor: 'pointer' }}
                                          >
                                            metadata
                                          </summary>
                                          <pre className="fs-8 mb-0">
                                            {JSON.stringify(it.metadata, null, 2)}
                                          </pre>
                                        </details>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>

                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-4 fs-7">
                    <div className="d-flex align-items-center gap-2 text-muted">
                      <span>Mostrar</span>
                      <select
                        className="form-select form-select-sm w-auto"
                        value={limit}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          setLimit(n);
                          load(applied, n, 0);
                        }}
                      >
                        {PAGE_SIZES.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                      <span>· {total.toLocaleString('es-MX')} eventos</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        disabled={offset === 0 || loading}
                        onClick={() => load(applied, limit, Math.max(0, offset - limit))}
                      >
                        <Icon icon="ki-left" size="fs-5" />
                      </button>
                      <span className="text-muted">
                        página {Math.floor(offset / limit) + 1} de {pages}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        disabled={offset + limit >= total || loading}
                        onClick={() => load(applied, limit, offset + limit)}
                      >
                        <Icon icon="ki-right" size="fs-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default AuditPage;
